import type { ProductionOrderSheetLine } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { productionOrderSheetTotal } from '@/entities/order';
import { formatCount, formatWon } from '@/shared/lib';
import { cn } from '@/shared/ui';

interface LinesTableProps {
  readonly lines: readonly ProductionOrderSheetLine[];
}

const CELL_CLASS = 'border border-foreground/20 px-3 py-2 align-top print:border-black';
const NUMBER_CLASS = 'text-right whitespace-nowrap tabular-nums';

/** 발주서 품목·단가 표 — 단가가 비면 단가·금액도 빈칸(0원으로 보이지 않게), 마지막 줄은 합계(VAT 별도). */
export const LinesTable = ({ lines }: LinesTableProps): ReactElement => {
  const total = productionOrderSheetTotal(lines);

  return (
    <table aria-label="품목 및 단가" className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th scope="col" className={cn(CELL_CLASS, 'w-12')}>
            No
          </th>
          <th scope="col" className={cn(CELL_CLASS, 'text-left')}>
            품목
          </th>
          <th scope="col" className={cn(CELL_CLASS, NUMBER_CLASS)}>
            수량
          </th>
          <th scope="col" className={cn(CELL_CLASS, NUMBER_CLASS)}>
            단가
          </th>
          <th scope="col" className={cn(CELL_CLASS, NUMBER_CLASS)}>
            금액
          </th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: 발주서 행은 id 가 없고 순서가 곧 번호다
          <tr key={index}>
            <th scope="row" className={cn(CELL_CLASS, 'text-center font-normal')}>
              {index + 1}
            </th>
            <td className={CELL_CLASS}>{line.name}</td>
            <td className={cn(CELL_CLASS, NUMBER_CLASS)}>{formatCount(line.quantity)}</td>
            <td className={cn(CELL_CLASS, NUMBER_CLASS)}>
              {line.unitPrice === null ? '' : formatWon(line.unitPrice)}
            </td>
            <td className={cn(CELL_CLASS, NUMBER_CLASS)}>
              {line.unitPrice === null ? '' : formatWon(line.quantity * line.unitPrice)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th scope="row" colSpan={4} className={cn(CELL_CLASS, 'text-right font-semibold')}>
            합계 (VAT 별도)
          </th>
          <td className={cn(CELL_CLASS, NUMBER_CLASS, 'font-semibold')}>
            {total === null ? '' : formatWon(total)}
          </td>
        </tr>
      </tfoot>
    </table>
  );
};
